import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useLocalization } from "@progress/kendo-react-intl";
import { Tooltip } from "@progress/kendo-react-tooltip";
import type { SVGIcon } from "@progress/kendo-svg-icons";
import {
  chevronDoubleUpIcon,
  chevronDownIcon,
  chevronUpIcon,
  equalIcon,
  minusIcon,
} from "@progress/kendo-svg-icons";
import moment from "moment";
import { ReactComponent as MlIcon } from "../../../../../Assets/ml-favicon-full.svg";
import { enMessages } from "../../../../../messages/en-US";
import { getStreamBadge, isOngoingEndDate } from "../Overview/Dialogs/AnomalyDetector/AnomalyDetectorForm.utils";
import { GetEndpointIcon, GetGatewayIcon } from "../../../../../Utilities/CustomIcons";
import type { InsightBackendPayload, InsightTableRow, ProcessedInsightRow } from "./types";

export interface FilterOption {
  text: string;
  value: string;
  iconContent?: React.ReactNode;
}

export type FilterSectionKey = "streams" | "criticality" | "type" | "source";

export const getCriticalityBadgeClassName = (criticality: string): string => {
  switch (criticality) {
    case "Critical":
      return "bg-[#780000] text-white";
    case "High":
      return "bg-[#dc0000] text-white";
    case "Medium":
      return "bg-[#fd8c00] text-white";
    case "Low":
      return "bg-[#92D050] text-gray-900";
    default:
      return "bg-gray-200 text-gray-700";
  }
};

/** Filter value for items without a criticality set. Starts unselected. */
export const NO_CRITICALITY_FILTER_VALUE = "none";

export const getCriticalityIconDetails = (
  criticality: string
): { icon: SVGIcon | null; color: string; label: string } => {
  switch (criticality) {
    case "Critical":
      return { icon: chevronDoubleUpIcon, color: "#780000", label: "Critical" };
    case "High":
      return { icon: chevronUpIcon, color: "#dc0000", label: "High" };
    case "Medium":
      return { icon: equalIcon, color: "#fd8c00", label: "Medium" };
    case "Low":
      return { icon: chevronDownIcon, color: "#fdc500", label: "Low" };
    default:
      return { icon: null, color: "#6b7280", label: criticality || "Unknown" };
  }
};

interface StreamIconDetails {
  badge: string;
  title: string;
}

const getStreamFallbackText = (label: string): string =>
  label
    .split(" ")
    .map((part) => part[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();

/** Every stream is marked by its unique badge, so all of them read the same way. */
export const getStreamIconDetails = (
  stream: string,
  localizedLabel: string,
  localizationService: ReturnType<typeof useLocalization>
): StreamIconDetails => ({
  // Initials of a translated label are not unique; only for values outside the vocabulary.
  badge: getStreamBadge(stream) ?? getStreamFallbackText(localizedLabel),
  title:
    stream === "voltage"
      ? localizationService.toLanguageString("custom.voltageTooltip", "Voltage")
      : localizedLabel,
});

/**
 * Marks what the row is about: a point or a device (e.g. gateway).
 * Both glyphs are tall and narrow, so the width follows each viewBox ratio instead of being squared off.
 */
const TARGET_ICON_HEIGHT = 16;

function renderTargetIcon(item: ProcessedInsightRow): React.ReactNode {
  // Nothing was resolved, so there is no target to mark.
  if (!item.endpointDisplay || item.endpointDisplay === "-") return null;

  return (
    <span className="flex shrink-0 items-center text-gray-400">
      {item.targetKind === "device"
        ? GetGatewayIcon(Math.round((46.2 / 67.6) * TARGET_ICON_HEIGHT), TARGET_ICON_HEIGHT)
        : GetEndpointIcon(Math.round((638 / 769) * TARGET_ICON_HEIGHT), TARGET_ICON_HEIGHT)}
    </span>
  );
}

export function renderCompactPeriod(
  item: ProcessedInsightRow,
  localizationService: ReturnType<typeof useLocalization>
): React.ReactNode {
  const start = moment(item.startDate);
  const end = moment(item.endDate);
  const ongoing = isOngoingEndDate(item.endDate);

  if (!start.isValid()) {
    return <div className="mt-0.5 text-[13px] font-medium leading-4 text-gray-900">-</div>;
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          {localizationService.toLanguageString("custom.start", enMessages.custom.start)}
        </div>
        <div className="mt-0.5 text-[12px] font-medium leading-4 text-gray-900">{start.format("DD MMM YYYY")}</div>
        <div className="text-[11px] leading-4 text-gray-500">{start.format("HH:mm")}</div>
      </div>
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          {localizationService.toLanguageString("custom.end", enMessages.custom.end)}
        </div>
        {ongoing || !end.isValid() ? (
          <div className="mt-0.5 text-[12px] font-medium leading-4 text-gray-900">-</div>
        ) : (
          <>
            <div className="mt-0.5 text-[12px] font-medium leading-4 text-gray-900">{end.format("DD MMM YYYY")}</div>
            <div className="text-[11px] leading-4 text-gray-500">{end.format("HH:mm")}</div>
          </>
        )}
      </div>
    </div>
  );
}

interface InsightTileProps {
  item: ProcessedInsightRow;
  selected: boolean;
  hidePointName: boolean;
  localizationService: ReturnType<typeof useLocalization>;
  streamLabelByValue: Map<string, string>;
  onClick: () => void;
}

export const InsightTile = ({
  item,
  selected,
  hidePointName,
  localizationService,
  streamLabelByValue,
  onClick,
}: InsightTileProps) => {
  const isMlSource = item.source === "ml";
  const criticalityDetails = getCriticalityIconDetails(item.criticality || "");
  const isAnchor = item.itemKind === "anchor";
  const streamIcons = (Array.isArray(item.streams) ? item.streams : []).map((stream) =>
    getStreamIconDetails(stream, streamLabelByValue.get(stream) ?? stream, localizationService)
  );
  const rightColumnClassName = hidePointName ? "w-[132px]" : "";
  const criticalitySlotClassName = "inline-flex h-6 w-6 items-center justify-center";

  const noCriticalityLabel = localizationService.toLanguageString("custom.noCriticality", "No criticality");

  const renderCriticalityIcon = () => {
    if (criticalityDetails.icon) {
      return (
        <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
          <span className={`${criticalitySlotClassName} rounded-md`} title={criticalityDetails.label}>
            <SvgIcon icon={criticalityDetails.icon} size="xxlarge" style={{ color: criticalityDetails.color }} />
          </span>
        </Tooltip>
      );
    }

    return (
      <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
        <span className={`${criticalitySlotClassName} rounded-md text-gray-400`} title={noCriticalityLabel}>
          <SvgIcon icon={minusIcon} size="xxlarge" />
        </span>
      </Tooltip>
    );
  };

  const MlChipTooltipContent = () => (
    <div className="p-2">
      <div className="font-bold mb-1">ML-generated</div>
      {item.anomalyId && <div>ID: {item.anomalyId}</div>}
    </div>
  );

  const renderMlChip = () => (
    <Tooltip anchorElement="target" position="bottom" parentTitle={true} content={MlChipTooltipContent}>
      <span className="inline-flex h-6 items-center justify-center" title=" ">
        <MlIcon className="h-3 w-auto" aria-label="ML" />
      </span>
    </Tooltip>
  );

  const criticalityContent = isAnchor ? (
    <span className={`${criticalitySlotClassName} rounded-md opacity-0`} aria-hidden="true">
      <SvgIcon icon={equalIcon} size="xxlarge" />
    </span>
  ) : isMlSource ? (
    <>
      {renderMlChip()}
      {renderCriticalityIcon()}
    </>
  ) : item.criticality ? (
    renderCriticalityIcon()
  ) : (
    <span className={`${criticalitySlotClassName} rounded-md opacity-0`} aria-hidden="true">
      <SvgIcon icon={equalIcon} size="xxlarge" />
    </span>
  );

  const buttonStyle: React.CSSProperties = selected
    ? {
        borderColor: "var(--kendo-color-primary)",
        boxShadow: "0 0 0 2px color-mix(in srgb, var(--kendo-color-primary) 18%, transparent)",
      }
    : {};

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-lg border px-3 py-2.5 text-left transition ${
        selected
          ? "bg-white shadow-md"
          : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 hover:shadow-sm"
      }`}
      style={Object.keys(buttonStyle).length > 0 ? buttonStyle : undefined}
    >
      {!hidePointName && (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 pr-1">
            <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
              <div
                className="flex max-w-full items-center gap-1.5 overflow-hidden text-[14px] font-semibold leading-5 text-gray-900"
                title={item.assetDisplay || item.endpointDisplay || "-"}
              >
                {/* Asset level: the target is the title, so the icon belongs here. */}
                {!item.assetDisplay && renderTargetIcon(item)}
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                  {item.assetDisplay || item.endpointDisplay || "-"}
                </span>
              </div>
            </Tooltip>
            {/* Site level: asset is the title, endpoint becomes the subtitle. */}
            {item.assetDisplay && item.endpointDisplay && item.endpointDisplay !== "-" && (
              <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
                <div
                  className="flex max-w-full items-center gap-1.5 overflow-hidden text-[12px] leading-4 text-gray-500"
                  title={item.endpointDisplay}
                >
                  {renderTargetIcon(item)}
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap">{item.endpointDisplay}</span>
                </div>
              </Tooltip>
            )}
          </div>
          <div className="flex h-6 min-w-[60px] shrink-0 items-center justify-end gap-1">{criticalityContent}</div>
        </div>
      )}
      <div className={`${hidePointName ? "" : ""}flex ${hidePointName ? "items-stretch" : "items-end"} justify-between gap-3`}>
        <div className="min-w-0 flex-1">{renderCompactPeriod(item, localizationService)}</div>
        {(criticalityContent || streamIcons.length > 0) && (
          <div
            className={`flex shrink-0 ${rightColumnClassName} ${
              hidePointName
                ? `self-stretch flex-col items-end justify-center gap-1`
                : "self-end"
            }`}
          >
            {hidePointName && criticalityContent && (
              <div className="flex items-start justify-end gap-1">{criticalityContent}</div>
            )}
            {streamIcons.length > 0 && (
              <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
                <div className="flex w-full flex-wrap content-start items-center justify-end gap-0.5">
                  {streamIcons.map((streamIcon) => (
                    <span
                      key={`${item.id}-${streamIcon.title}`}
                      className="inline-flex h-5 w-5 items-center justify-center rounded-md text-[9px] font-semibold leading-none tracking-tight text-gray-700"
                      title={streamIcon.title}
                    >
                      {streamIcon.badge}
                    </span>
                  ))}
                </div>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </button>
  );
};

interface FilterSectionProps {
  title: string;
  expanded: boolean;
  options: FilterOption[];
  selectedValues: string[];
  onToggleExpanded: () => void;
  onToggleAll: () => void;
  onToggleValue: (value: string) => void;
}

export const FilterSection = ({
  title,
  expanded,
  options,
  selectedValues,
  onToggleExpanded,
  onToggleAll,
  onToggleValue,
}: FilterSectionProps) => {
  const areAllSelected = options.length > 0 && options.every((option) => selectedValues.includes(option.value));

  return (
    <div className="border-b border-gray-100 py-1 last:border-b-0">
      <div className="flex items-center justify-between gap-2 px-2 py-1.5">
        <Button
          fillMode="flat"
          themeColor="base"
          onClick={onToggleExpanded}
          className="!flex !min-w-0 !items-center !justify-start !rounded-md !px-0 !py-0 !text-left hover:!bg-transparent"
        >
          <span className="flex items-center gap-2 text-sm text-gray-800">
            <span
              className="inline-flex h-4 w-4 items-center justify-center text-gray-600 transition-transform"
              style={{ transform: expanded ? "rotate(0deg)" : "rotate(-90deg)" }}
            >
              <SvgIcon icon={chevronDownIcon} size="small" />
            </span>
          </span>
        </Button>
        <Button
          fillMode="flat"
          themeColor="base"
          onClick={onToggleAll}
          className="!flex !min-w-0 !flex-1 !items-center !justify-start !rounded-md !px-0 !py-0 hover:!bg-transparent"
        >
          <span className="flex min-w-0 items-center gap-2 text-sm text-gray-800">
            <Checkbox checked={areAllSelected} onChange={() => {}} />
            <span className="font-medium">{title}</span>
          </span>
        </Button>
      </div>
      {expanded && (
        <div className="pt-1">
          {options.map((option) => {
            const isSelected = selectedValues.includes(option.value);

            return (
              <Button
                key={option.value}
                fillMode="flat"
                themeColor="base"
                onClick={() => onToggleValue(option.value)}
                className="!flex !w-full !items-center !justify-start !rounded-md !px-16 !py-1 !text-left hover:!bg-gray-50"
              >
                <span className="flex items-center gap-2 text-sm text-gray-700">
                  <Checkbox checked={isSelected} onChange={() => {}} />
                  {option.iconContent ? (
                    <span className="inline-flex h-4 w-4 items-center justify-center text-gray-700">{option.iconContent}</span>
                  ) : null}
                  <span>{option.text}</span>
                </span>
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
};

function parsePeriodToDates(period: string): { startDate: string; endDate: string } | null {
  const match = period.match(/^(.+?)\s*-\s*(.+)$/);
  if (!match) return null;
  const startStr = match[1].trim();
  const endStr = match[2].trim();
  const startM = moment(startStr, ["DD MMM YYYY HH:mm", "D MMM YYYY HH:mm"]);
  const endM = moment(endStr, ["DD MMM YYYY HH:mm", "D MMM YYYY HH:mm"]);
  if (!startM.isValid() || !endM.isValid()) return null;
  return {
    startDate: startM.toISOString(),
    endDate: endM.toISOString(),
  };
}

export function insightRowToBackendPayload(
  row: InsightTableRow,
  assetId: string,
  author?: string,
  authorId?: string
): InsightBackendPayload {
  const dates = parsePeriodToDates(row.period);
  const fallback = {
    startDate: moment().subtract(1, "day").toISOString(),
    endDate: moment().toISOString(),
  };

  const resolvedAuthor = author ?? row.author;
  return {
    machineId: assetId,
    pointId: row.pointId ?? row.id,
    user: resolvedAuthor ?? "",
    userId: authorId ?? "",
    startDate: dates?.startDate ?? fallback.startDate,
    endDate: dates?.endDate ?? fallback.endDate,
    anomalyId: row.id,
    streams: row.streams ?? [],
    form: {
      criticality: row.criticality ?? "",
      indicators: row.indicator ? [row.indicator] : [],
      rootCauses: row.rootCause ? [row.rootCause] : [],
      actions: row.action ? [row.action] : [],
      confidence: row.confidence ?? 0,
      estimatedSavings: row.estimatedSavings ?? 0,
    },
    notes: row.notes
      ? [
          {
            timestamp: new Date().toISOString(),
            user: resolvedAuthor ?? "",
            comment: row.notes,
          },
        ]
      : [],
    revisionHistory: [],
    mlResults: [],
  };
}
